import React from 'react';
import { Cookie, Settings, BarChart, Target, Shield } from 'lucide-react';

const Cookies = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
            <Cookie className="h-8 w-8 text-orange-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Çerez Politikası</h1>
          <p className="text-gray-600">
            Son güncelleme: {new Date().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Cookie className="h-6 w-6 text-orange-600 mr-2" />
              1. Çerez Nedir?
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Çerezler (cookies), web sitelerini ziyaret ettiğinizde tarayıcınız tarafından bilgisayarınıza veya mobil 
              cihazınıza kaydedilen küçük metin dosyalarıdır. Çerezler, web sitelerinin işleyişini iyileştirmek, 
              kullanıcı deneyimini geliştirmek ve belirli işlevleri sağlamak için kullanılır.
            </p>
            <p className="text-gray-700 leading-relaxed">
              LeaserTech web sitesini kullanarak, çerez politikamızı kabul etmiş sayılırsınız. Ancak tarayıcı 
              ayarlarınızdan çerezleri yönetebilir veya devre dışı bırakabilirsiniz.
            </p>
          </section>

          {/* Cookie Types */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="h-6 w-6 text-orange-600 mr-2" />
              2. Kullandığımız Çerez Türleri
            </h2>
            
            <div className="space-y-6">
              {/* Zorunlu Çerezler */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Zorunlu Çerezler</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Bu çerezler, web sitemizin temel işlevlerini yerine getirmesi için mutlaka gereklidir. 
                  Bu çerezler olmadan web sitesi düzgün çalışamaz.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Oturum yönetimi ve güvenlik çerezleri</li>
                  <li>Sepet (alışveriş sepeti) bilgilerini saklama</li>
                  <li>Kullanıcı kimlik doğrulama çerezleri</li>
                  <li>Tercih ve ayarların kaydedilmesi</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Süre:</strong> Bu çerezler oturum süresince (tarayıcı kapatılana kadar) veya 
                  kalıcı olarak (belirli bir süre) saklanabilir.
                </p>
              </div>

              {/* Analitik Çerezler */}
              <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <BarChart className="h-5 w-5 text-green-600 mr-2" />
                  Analitik Çerezler
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Bu çerezler, web sitemizin nasıl kullanıldığını anlamamıza yardımcı olur. 
                  Ziyaretçi sayısı, hangi sayfaların daha çok ziyaret edildiği, kullanıcı davranışları 
                  gibi bilgileri toplarız.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Google Analytics çerezleri</li>
                  <li>Sayfa görüntüleme istatistikleri</li>
                  <li>Kullanıcı davranış analizi</li>
                  <li>Site performans ölçümleri</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Süre:</strong> Bu çerezler genellikle 2 yıl boyunca saklanır.
                </p>
                <p className="text-gray-700 leading-relaxed mt-2 text-sm">
                  <strong>Not:</strong> Google Analytics çerezlerini devre dışı bırakmak için{' '}
                  <a 
                    href="https://tools.google.com/dlpage/gaoptout" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-orange-600 hover:text-orange-700 underline"
                  >
                    Google Analytics Opt-out Browser Add-on
                  </a>{' '}
                  kullanabilirsiniz.
                </p>
              </div>

              {/* Fonksiyonel Çerezler */}
              <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Fonksiyonel Çerezler</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Bu çerezler, web sitemizde daha iyi bir deneyim sunmak için kullanılır. 
                  Tercihlerinizi hatırlar ve size kişiselleştirilmiş içerik sunar.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Dil tercihleri</li>
                  <li>Bölge/konum tercihleri</li>
                  <li>Kullanıcı arayüzü tercihleri</li>
                  <li>Favori ürünler ve geçmiş aramalar</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Süre:</strong> Bu çerezler genellikle 1 yıl boyunca saklanır.
                </p>
              </div>

              {/* Pazarlama Çerezleri */}
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <Target className="h-5 w-5 text-yellow-600 mr-2" />
                  Pazarlama ve Reklam Çerezleri
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Bu çerezler, size ilgi alanlarınıza uygun reklamlar ve içerikler göstermek için kullanılır. 
                  Ayrıca reklam kampanyalarımızın etkinliğini ölçmemize yardımcı olur.
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>İlgi alanına dayalı reklamlar</li>
                  <li>Retargeting (yeniden hedefleme) çerezleri</li>
                  <li>Sosyal medya entegrasyon çerezleri</li>
                  <li>Dönüşüm takibi</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Süre:</strong> Bu çerezler genellikle 90 gün ile 2 yıl arasında saklanır.
                </p>
              </div>
            </div>
          </section>

          {/* Third Party Cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              3. Üçüncü Taraf Çerezleri
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Web sitemizde aşağıdaki üçüncü taraf hizmetlerinin çerezlerini kullanıyoruz:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>
                <strong>Google Analytics:</strong> Web sitesi trafiğini analiz etmek için kullanılır. 
                Google'ın gizlilik politikası için{' '}
                <a 
                  href="https://policies.google.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-orange-600 hover:text-orange-700 underline"
                >
                  buraya tıklayın
                </a>.
              </li>
              <li>
                <strong>Google Search Console:</strong> Arama performansını izlemek için kullanılır.
              </li>
              <li>
                <strong>Sosyal Medya Platformları:</strong> Facebook, Twitter, Instagram gibi platformların 
                entegrasyonu için kullanılır.
              </li>
              <li>
                <strong>Ödeme Sağlayıcıları:</strong> PaynKolay gibi ödeme işlemlerini gerçekleştirmek için 
                kullanılır.
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Bu üçüncü taraf çerezlerin kullanımı, ilgili üçüncü tarafların kendi gizlilik politikalarına tabidir.
            </p>
          </section>

          {/* Cookie Management */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="h-6 w-6 text-orange-600 mr-2" />
              4. Çerezleri Yönetme
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Çerezleri yönetmek veya silmek için tarayıcınızın ayarlarını kullanabilirsiniz. 
              Çoğu tarayıcı çerezleri otomatik olarak kabul eder, ancak tarayıcı ayarlarınızdan 
              çerezleri reddetme veya yalnızca belirli çerezleri kabul etme seçeneğiniz vardır.
            </p>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Popüler Tarayıcılar için Çerez Ayarları:</h3>
              <ul className="space-y-3 text-gray-700">
                <li>
                  <strong>Google Chrome:</strong> Ayarlar → Gizlilik ve güvenlik → Çerezler ve diğer site verileri
                </li>
                <li>
                  <strong>Mozilla Firefox:</strong> Seçenekler → Gizlilik ve Güvenlik → Çerezler ve site verileri
                </li>
                <li>
                  <strong>Safari:</strong> Tercihler → Gizlilik → Çerezleri ve web sitesi verilerini yönet
                </li>
                <li>
                  <strong>Microsoft Edge:</strong> Ayarlar → Gizlilik, arama ve hizmetler → Çerezler ve site izinleri
                </li>
                <li>
                  <strong>Opera:</strong> Ayarlar → Gizlilik ve güvenlik → Çerezler
                </li>
              </ul>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed">
                <strong>Önemli Not:</strong> Bazı çerezleri devre dışı bırakmak veya silmek, web sitemizin 
                bazı özelliklerinin düzgün çalışmamasına neden olabilir. Özellikle zorunlu çerezleri devre 
                dışı bırakmak, web sitesinin temel işlevlerini kullanmanızı engelleyebilir.
              </p>
            </div>
          </section>

          {/* Cookie Preferences */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              5. Çerez Tercihleriniz
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Web sitemizi ziyaret ettiğinizde, çerez tercihlerinizi seçebilirsiniz. Zorunlu çerezler her 
              durumda kullanılır, ancak analitik, fonksiyonel ve pazarlama çerezleri için tercih belirtebilirsiniz.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Çerez tercihlerinizi istediğiniz zaman değiştirebilirsiniz. Tarayıcınızın ayarlarından veya 
              web sitemizin çerez ayarları sayfasından tercihlerinizi güncelleyebilirsiniz.
            </p>
          </section>

          {/* Updates */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              6. Çerez Politikası Güncellemeleri
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Bu Çerez Politikasını zaman zaman güncelleyebiliriz. Önemli değişiklikler yaptığımızda, 
              web sitemizde veya e-posta yoluyla sizi bilgilendireceğiz. Politikanın en güncel versiyonu 
              her zaman bu sayfada yer alacaktır.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              7. İletişim
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Çerez politikamız hakkında sorularınız için bizimle iletişime geçebilirsiniz:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>E-posta:</strong> info@leasertech.com</p>
              <p><strong>Telefon:</strong> +90 538 316 1462</p>
              <p><strong>Adres:</strong> İstanbul, Türkiye</p>
              <p className="mt-4">
                <a href="/contact" className="text-orange-600 hover:text-orange-700 underline font-medium">
                  İletişim sayfamıza git →
                </a>
              </p>
            </div>
          </section>

        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-gray-600 text-sm">
          <p>
            Bu çerez politikası, Türkiye Cumhuriyeti'nin 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) 
            ve Avrupa Birliği Genel Veri Koruma Tüzüğü (GDPR) gerekliliklerine uygun olarak hazırlanmıştır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Cookies;





